// src/layouts/SuperAdminLayout.jsx
import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "../styles/sa-admin.css";
import logo from "../assets/logo.svg";
import mobileHeaderLogo from "../assets/mobile-header-logo.svg";
import { resolveAdminPageTitle } from "./adminPageTitle";

import {
  LayoutDashboard,
  Volleyball,
  MessagesSquare,
  MapPinHouse,
  LogOut,
  Users,
  CalendarDays,
  CreditCard,
  WalletCards,
  BadgePercent,
  Image,
  BellRing,
  UserCircle,
  Ban,
  TicketPercent,
} from "lucide-react";

const SuperAdminLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pageTitle = resolveAdminPageTitle(pathname);

  // ✅ NEW: sidebar open/close for mobile drawer
  const [sbOpen, setSbOpen] = React.useState(false);

  const scrollYRef = React.useRef(0);


  // ✅ NEW: close sidebar on route change (mobile)
  React.useEffect(() => {
    setSbOpen(false);
  }, [pathname]);

  // ✅ LOCK BACKGROUND SCROLL when sidebar drawer is open (mobile)
  // ✅ LOCK BACKGROUND SCROLL when sidebar drawer is open (mobile) — lock main.sa-body
  React.useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 900px)").matches;
    if (!isMobile) return;

    const scrollEl = document.querySelector("main.sa-body");
    if (!scrollEl) return;

    // store previous inline styles
    const prev = {
      overflow: scrollEl.style.overflow,
    };

    if (sbOpen) {
      scrollYRef.current = scrollEl.scrollTop || 0;

      scrollEl.style.overflow = "hidden";
    } else {
      scrollEl.style.overflow = prev.overflow || "";
      scrollEl.scrollTop = scrollYRef.current || 0;
    }

    return () => {
      scrollEl.style.overflow = prev.overflow || "";
      if (sbOpen) scrollEl.scrollTop = scrollYRef.current || 0;
    };
  }, [sbOpen]);



  // ---------- ROLE + BASE PATH DETECTION ----------

  // Base path is decided from current URL
  let basePath = "/super-admin";
  if (pathname.startsWith("/venue-admin")) {
    basePath = "/venue-admin";
  } else if (pathname.startsWith("/country-admin")) {
    basePath = "/country-admin";
  }

  // Role from storage (authoritative)
  let role = "superadmin";
  try {
    const ctx = JSON.parse(localStorage.getItem("va_ctx") || "{}");
    if (ctx.role) {
      role = ctx.role;
    } else if (pathname.startsWith("/venue-admin")) {
      role = "venue_admin";
    } else if (pathname.startsWith("/country-admin")) {
      role = "country_admin";
    }
  } catch (_) {
    // fall back to path-based inference
    if (pathname.startsWith("/venue-admin")) {
      role = "venue_admin";
    } else if (pathname.startsWith("/country-admin")) {
      role = "country_admin";
    }
  }

  const isVenueAdmin = role === "venue_admin";
  const isCountryAdmin = role === "country_admin";
  const isSuperAdmin = !isVenueAdmin && !isCountryAdmin;

  const nav = (subpath) => {
    setSbOpen(false);
    navigate(`${basePath}${subpath}`);
  };

  const isActive = (subpath) =>
    pathname === `${basePath}${subpath}` ||
    pathname.startsWith(`${basePath}${subpath}/`);

  const logout = () => {
    localStorage.removeItem("sa_jwt");
    localStorage.removeItem("va_ctx");
    navigate(`${basePath}/login?force=1`, { replace: true });
  };

  // --- Role label + badge ---
  let roleLabel = "Super Admin";
  let badgeLabel = "SA";
  if (isVenueAdmin) {
    roleLabel = "Venue Admin";
    badgeLabel = "VA";
  } else if (isCountryAdmin) {
    roleLabel = "Country Admin";
    badgeLabel = "CA";
  }

  // ✅ Browser tab title: Staging vs Production + role
  React.useEffect(() => {
    const host = window.location.hostname || "";

    // Your domains:
    // Staging: staging.getonground.com, staging-admin.getonground.com
    // Prod:    getonground.com, www.getonground.com, admin.getonground.com
    const isStaging =
      host === "staging.getonground.com" ||
      host === "staging-admin.getonground.com" ||
      host.startsWith("staging-") ||
      host.includes("staging.");

    const prefix = isStaging ? "Staging " : "";
    document.title = `${prefix}${roleLabel} | Onground`;
  }, [roleLabel]);

  return (
    <div className="sa-admin sa-slim">
      {/* ===== Sidebar ===== */}
      <aside
        className={`sa-sb sa-sb--mobilefix ${sbOpen ? "is-open" : ""}`}
        style={{
          display: "grid",
          gridTemplateRows: "auto 1fr auto", //  brand fixed, nav scroll, logout fixed
          height: "calc(var(--app-height, 100dvh) * 1)",
          paddingTop: "var(--safe-top)",
          paddingBottom: "var(--safe-bottom)",

          overflow: "hidden",               //  keep clipping inside sidebar only
        }}

      >


        <div className="sa-sb-brand">
          <img src={logo} alt="OnGround" className="sa-brand sa-brand--sb" />
          <small className="sa-role">{roleLabel}</small>
        </div>

        {/* ===== Nav ===== */}
        <nav
          className="sa-sb-nav"
          style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: 12, // spacing above logout
          }}
        >


          {/* Dashboard – visible to all */}
          <div
            className={`sa-item ${isActive("/dashboard") ? "active" : ""}`}
            onClick={() => nav("/dashboard")}
            role="button"
          >
            <LayoutDashboard className="lucide sa-ic-menu" />
            <span>Dashboard</span>
          </div>

          {/* Banners — allowed for Super Admin + Country Admin (not Venue Admin) */}
          {!isVenueAdmin && (
            <div
              className={`sa-item ${isActive("/banners") ? "active" : ""}`}
              onClick={() => nav("/banners")}
              role="button"
            >
              <Image className="lucide sa-ic-menu" />
              <span>Banners</span>
            </div>
          )}

          {/* Games — allowed for Super Admin + Country Admin (not Venue Admin) */}
          {!isVenueAdmin && (
            <div
              className={`sa-item ${isActive("/games") ? "active" : ""}`}
              onClick={() => nav("/games")}
              role="button"
            >
              <Volleyball className="lucide sa-ic-menu" />
              <span>Games</span>
            </div>
          )}
          {/* Local Games / Find Players — allowed for Super Admin + Country Admin */}
          {!isVenueAdmin && (
            <div
              className={`sa-item ${isActive("/local-games") ? "active" : ""}`}
              onClick={() => nav("/local-games")}
              role="button"
            >
              <MessagesSquare className="lucide sa-ic-menu" />
              <span>Local Games</span>
            </div>
          )}

          {/* Push Notifications — allowed for Super Admin + Country Admin (not Venue Admin) */}
          {!isVenueAdmin && (
            <div
              className={`sa-item ${isActive("/push-notifications") ? "active" : ""
                }`}
              onClick={() => nav("/push-notifications")}
              role="button"
            >
              <BellRing className="lucide sa-ic-menu" />
              <span>Push Notifications</span>
            </div>
          )}

          {/* Venues – visible to all admin roles */}
          <div
            className={`sa-item ${isActive("/venues") ? "active" : ""}`}
            onClick={() => nav("/venues")}
            role="button"
          >
            <MapPinHouse className="lucide sa-ic-menu" />
            <span>Venues</span>
          </div>

          {/* Reservations – visible to all admin roles */}
          <div
            className={`sa-item ${isActive("/reservations") ? "active" : ""}`}
            onClick={() => nav("/reservations")}
            role="button"
          >
            <CalendarDays className="lucide sa-ic-menu" />
            <span>Bookings</span>
          </div>
          <div
            className={`sa-item ${isActive("/block-slot") ? "active" : ""}`}
            onClick={() => nav("/block-slot")}
            role="button"
          >
            <Ban className="lucide sa-ic-menu" />
            <span>Block Slot</span>
          </div>
          {/* <div
            className={`sa-item ${isActive("/available-slots") ? "active" : ""}`}
            onClick={() => nav("/available-slots")}
            role="button"
          >
            <Clock3 className="lucide sa-ic-menu" />
            <span>Available Slots</span>
          </div> */}
          



          {/* Users — only for SUPER admin */}
          {isSuperAdmin && (
            <div
              className={`sa-item ${isActive("/users") ? "active" : ""}`}
              onClick={() => nav("/users")}
              role="button"
            >
              <Users className="lucide sa-ic-menu" />
              <span>Users</span>
            </div>
          )}

          {/* Country Admins — only for SUPER admin */}
          {isSuperAdmin && (
            <div
              className={`sa-item ${isActive("/country-admins") ? "active" : ""
                }`}
              onClick={() => nav("/country-admins")}
              role="button"
            >
              <Users className="lucide sa-ic-menu" />
              <span>Country Admins</span>
            </div>
          )}

          {!isVenueAdmin && (
            <div
              className={`sa-item ${isActive("/promo-codes") ? "active" : ""}`}
              onClick={() => nav("/promo-codes")}
              role="button"
            >
              <TicketPercent className="lucide sa-ic-menu" />
              <span>Promo Codes</span>
            </div>
          )}

          {/* Settlements — visible to all */}
          <div
            className={`sa-item ${isActive("/settlements") ? "active" : ""}`}
            onClick={() => nav("/settlements")}
            role="button"
          >
            <WalletCards className="lucide sa-ic-menu" />
            <span>Settlements</span>
          </div>

          {/* Payments — visible to all */}
          <div
            className={`sa-item ${isActive("/payments") ? "active" : ""}`}
            onClick={() => nav("/payments")}
            role="button"
          >
            <CreditCard className="lucide sa-ic-menu" />
            <span>Payments</span>
          </div>

          {/* Commission — visible to all */}
          <div
            className={`sa-item ${isActive("/commission") ? "active" : ""}`}
            onClick={() => nav("/commission")}
            role="button"
          >
            <BadgePercent className="lucide sa-ic-menu" />
            <span>Commission</span>
          </div>

          {/* Profile — visible to all */}
          <div
            className={`sa-item ${isActive("/profile") ? "active" : ""}`}
            onClick={() => nav("/profile")}
            role="button"
          >
            <UserCircle className="lucide sa-ic-menu" />
            <span>Profile</span>
          </div>
        </nav>

        {/* Logout */}
        <button
          type="button"
          className="sa-sb-logout"
          onClick={logout}
          title="Logout"
          style={{
            marginTop: "auto",      // pushes it to bottom
            flexShrink: 0,
            position: "sticky",     // keeps it visible even while nav scrolls
            bottom: 0,
            background: "#fff",     // prevent overlap transparency
            paddingTop: 12,
            paddingBottom: 12,
            zIndex: 2,
            borderTop: "1px solid rgba(0,0,0,0.08)",
          }}
        >

          <LogOut className="lucide sa-ic-menu" />
          <span>Logout</span>
        </button>

      </aside>

      {/* ✅ NEW: Mobile overlay */}
      <div
        className={`sa-overlay ${sbOpen ? "is-open" : ""}`}
        onClick={() => setSbOpen(false)}
      />

      {/* ===== Main ===== */}
      <div className="sa-main">
        <header
          className="sa-top"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* ✅ NEW: mobile menu button */}
          <button
            className="sa-menu-btn"
            type="button"
            onClick={() => setSbOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            ☰
          </button>

          <img
            src={logo}
            alt="OnGround"
            className="sa-top-brand sa-top-brand--desktop"
          />

          <img
            src={mobileHeaderLogo}
            alt="OnGround"
            className="sa-top-brand-mobile"
          />

          {pageTitle && (
            <h1 className="sa-top-title">{pageTitle}</h1>
          )}

          {/* RIGHT: badge only */}
          <div className="sa-user">
            <span className="sa-badge">{badgeLabel}</span>
          </div>
        </header>

        <main className="sa-body">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
